/**
 * Every choice the Studio offers on a dashboard banner, kept once: the Studio
 * lists them (`studio/schemaTypes/dashboard/dashboardBanner.ts`) and the
 * mapper drops a banner holding anything else, so a value added in one place
 * only would publish a banner that never shows.
 *
 * Plain data with no imports: the Studio, a package of its own, reads this
 * file. Each template's styles must be ones its component draws; the types
 * the components take (`bannerVariants.ts`) check that where banners are drawn.
 */
export const BANNER_TEMPLATES = [
  { title: "Top bar", value: "topBar" },
  { title: "Inline", value: "inline" },
  { title: "Sticky bottom bar", value: "stickyBottomBar" },
  { title: "Pop-up", value: "popUp" },
  { title: "Slide-in", value: "slideIn" },
] as const;

export const BANNER_STYLES = [
  { title: "Promo", value: "promo" },
  { title: "Soft", value: "soft" },
  { title: "Subtle", value: "subtle" },
  { title: "Dark", value: "dark" },
] as const;

/** The styles each template is designed in; the Pop-up has one look. */
export const TEMPLATE_STYLES = {
  topBar: ["promo", "subtle", "dark"],
  inline: ["promo", "soft", "subtle"],
  stickyBottomBar: ["promo", "subtle", "dark"],
  slideIn: ["promo", "subtle", "dark"],
} as const;

/**
 * The templates that float over the page, in one overlay: at most one of them
 * shows at a time, and each opens on a trigger rather than straight away.
 */
export const OVERLAY_TEMPLATES = ["popUp", "slideIn"] as const;

export const TOP_BAR_ICONS = [{ title: "Tag", value: "tag" }] as const;

export const TOP_BAR_POSITIONS = [
  { title: "In the page", value: "inPage" },
  { title: "Sticky under the app bar", value: "sticky" },
] as const;

/**
 * The places in dashboard pages an Inline banner can go, each one an
 * `InlineSlot` component in that page. Adding one is an entry here and the
 * component where it sits.
 */
export const INLINE_BANNER_SLOTS = [
  { title: "Dashboard: under the plan card", value: "dashboardUnderPlan" },
] as const;

/** What an Inline or Slide-in banner shows beside its text: an icon, a picture or nothing. */
export const BANNER_VISUALS = [
  { title: "Icon", value: "icon" },
  { title: "Image", value: "image" },
  { title: "None", value: "none" },
] as const;

/** The Pop-up's choice, which can also be the offer with its promo code. */
export const POP_UP_VISUALS = [
  { title: "Icon", value: "icon" },
  { title: "Image", value: "image" },
  { title: "Offer", value: "offer" },
  { title: "None", value: "none" },
] as const;

export const INLINE_ICONS = [
  { title: "Tag", value: "tag" },
  { title: "Calendar", value: "calendar" },
] as const;

export const STICKY_BOTTOM_BAR_ICONS = [
  { title: "Tag", value: "tag" },
] as const;

/** The icons a Pop-up or Slide-in can show in its tile. */
export const OVERLAY_ICONS = [
  { title: "Tag", value: "tag" },
  { title: "Calendar", value: "calendar" },
  { title: "Refer a friend", value: "referral" },
] as const;

/** What opens a Pop-up or Slide-in. */
export const BANNER_TRIGGERS = [
  { title: "On entry, after a delay", value: "entry" },
  { title: "When about to leave", value: "exitIntent" },
  { title: "Off", value: "off" },
] as const;

/** Whether an experiment's banners show on every page or on the pages listed. */
export const BANNER_PAGE_SCOPES = [
  { title: "Every page", value: "all" },
  { title: "Only these pages", value: "listed" },
] as const;

/** How a listed page is matched against the page the reader is on. */
export const BANNER_PAGE_MATCHES = [
  { title: "Starts with", value: "startsWith" },
  { title: "Exact", value: "exact" },
  { title: "Contains", value: "contains" },
] as const;

export type BannerPageMatch = (typeof BANNER_PAGE_MATCHES)[number]["value"];

/** What a banner's countdown runs to; a banner without one leaves it empty. */
export const COUNTDOWN_KINDS = [
  { title: "From the first view", value: "session" },
  { title: "To a date", value: "deadline" },
] as const;

/** What a banner shows once its countdown has run out. */
export const COUNTDOWN_ENDINGS = [
  { title: "Hide the banner", value: "hide" },
  { title: "Replace the message", value: "replace" },
  { title: "Disable the offer", value: "disable" },
] as const;

export const CLOSE_PERIODS = [
  { title: "Until the browser is closed", value: "session" },
  { title: "1 day", value: "1d" },
  { title: "7 days", value: "7d" },
  { title: "30 days", value: "30d" },
  { title: "Never show again", value: "forever" },
] as const;

type ValueOf<T extends ReadonlyArray<{ value: string }>> = T[number]["value"];

export type InlineBannerSlot = ValueOf<typeof INLINE_BANNER_SLOTS>;
export type InlineBannerIcon = ValueOf<typeof INLINE_ICONS>;
export type TopBarIcon = ValueOf<typeof TOP_BAR_ICONS>;
export type StickyBottomBarIcon = ValueOf<typeof STICKY_BOTTOM_BAR_ICONS>;
export type OverlayIcon = ValueOf<typeof OVERLAY_ICONS>;
export type OverlayTemplate = (typeof OVERLAY_TEMPLATES)[number];
export type TopBarPosition = ValueOf<typeof TOP_BAR_POSITIONS>;
export type BannerClosePeriod = ValueOf<typeof CLOSE_PERIODS>;
export type CountdownEnding = ValueOf<typeof COUNTDOWN_ENDINGS>;
export type TemplateStyle<T extends keyof typeof TEMPLATE_STYLES> =
  (typeof TEMPLATE_STYLES)[T][number];
